import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { BarChart2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { FamilyCompleteness } from '@/services/dashboardService';
import { useDashboardTranslations } from '@/hooks/useDashboardTranslations';
import { useTheme } from '@/contexts/ThemeContext';

interface MissingField {
  attribute_label: string;
  missing_count: number;
}

interface DataCompletenessCardProps {
  className?: string;
  completeness?: number;
  mostMissingFields?: MissingField[];
  loading?: boolean;
  attributesMissingCount?: number;
  mandatoryAttributes?: string[];
  familyCompleteness?: FamilyCompleteness[];
}

export function DataCompletenessCard({
  className,
  completeness,
  mostMissingFields: _mostMissingFields,
  loading,
  attributesMissingCount: _attributesMissingCount,
  mandatoryAttributes: _mandatoryAttributes,
  familyCompleteness = [],
}: DataCompletenessCardProps) {
  const i18n = useDashboardTranslations();
  const { actualTheme } = useTheme();

  // Process family completeness data: sort by lowest completeness first and limit to 6
  const processedFamilyData = React.useMemo(() => {
    if (!Array.isArray(familyCompleteness) || familyCompleteness.length === 0) {
      return [];
    }

    return familyCompleteness
      .slice() // Create a copy to avoid mutating the original array
      .sort((a, b) => (a.percentage || 0) - (b.percentage || 0)) // Sort by lowest percentage first
      .slice(0, 6); // Take only the first 6 (least completed)
  }, [familyCompleteness]);

  const hasData = processedFamilyData.length > 0;
  const overall = Number((completeness || 0).toFixed(2));

  // Theme-aware colors
  const barColor = actualTheme === 'dark' ? '#60a5fa' : '#1c2240'; // Light blue for dark mode, dark blue for light mode
  const tooltipCursorColor = actualTheme === 'dark' ? 'rgba(55, 65, 81, 0.5)' : '#f3f4f6'; // Dark gray with opacity for dark mode, light gray for light mode

  return (
    <Card className={`h-full ${className || ''}`}>
      <CardHeader>
        <CardTitle className="text-base font-semibold">
          {i18n.getDataCompletenessText('title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="h-[calc(100%-76px)] overflow-hidden">
        <div className="mb-2">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {i18n.getDataCompletenessText('completeness')}
            </span>
            <span className="text-sm font-medium dark:text-foreground">{overall}%</span>
          </div>
          {loading ? (
            <Skeleton className="h-3 w-full rounded" />
          ) : (
            <Progress value={overall} className="h-3" />
          )}
        </div>

        <div>
          <div className="mb-2 flex items-center gap-2">
            <BarChart2 className="size-4 text-primary-600 dark:text-primary" />
            <span className="text-xs font-semibold text-muted-foreground">
              {i18n.getDataCompletenessText('completenessByFamily')}
            </span>
          </div>
          {loading ? (
            <div className="flex h-48 items-center justify-center">
              <Skeleton className="h-40 w-full rounded" />
            </div>
          ) : !hasData ? (
            <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
              {i18n.getDataCompletenessText('noData')}
            </div>
          ) : (
            <div
              className="h-56 w-full overflow-hidden"
              role="img"
              aria-label={i18n.getDataCompletenessText('chartAriaLabel')}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={processedFamilyData}
                  layout="vertical"
                  margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                  barCategoryGap={processedFamilyData.length === 1 ? '10%' : 12}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    type="number"
                    domain={[0, 100]}
                    tick={{ fontSize: 11 }}
                    tickFormatter={v => `${Number(v.toFixed(2))}%`}
                  />
                  <YAxis
                    dataKey="familyName"
                    type="category"
                    width={80}
                    tick={{ fontSize: 11 }}
                    tickFormatter={(value: string) => {
                      // Truncate long family names to fit
                      return value.length > 12 ? `${value.substring(0, 12)}...` : value;
                    }}
                  />
                  <Tooltip
                    cursor={{ fill: tooltipCursorColor }}
                    formatter={(value: number) => [
                      i18n.getDataCompletenessText('percentComplete', {
                        value: Number(value.toFixed(2)),
                      }),
                      '',
                    ]}
                    labelFormatter={label => i18n.getDataCompletenessText('familyLabel', { label })}
                    contentStyle={{
                      backgroundColor: actualTheme === 'dark' ? '#1f2937' : '#ffffff',
                      border: actualTheme === 'dark' ? '1px solid #374151' : '1px solid #e5e7eb',
                      borderRadius: '6px',
                      color: actualTheme === 'dark' ? '#f9fafb' : '#111827',
                    }}
                  />
                  <Bar
                    dataKey="percentage"
                    fill={barColor}
                    radius={[4, 4, 4, 4]}
                    isAnimationActive={false}
                    barSize={processedFamilyData.length === 1 ? 40 : undefined}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
